import React, { useState, useEffect } from "react";

const MutualFundCalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState(100000);
  const [annualReturnRate, setAnnualReturnRate] = useState(12);
  const [investmentPeriod, setInvestmentPeriod] = useState(10);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
  });

  // Mutual Fund Calculation Function
  const calculateMutualFund = () => {
    const P = parseFloat(investmentAmount);
    const r = parseFloat(annualReturnRate) / 100;
    const n = parseFloat(investmentPeriod);
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(P) || isNaN(r) || isNaN(n) || P <= 0 || r <= 0 || n <= 0) return;

    // Compound Interest Formula: A = P(1 + r)^n
    const futureValue = P * Math.pow(1 + r, n);
    const totalInvestment = P;
    const estimatedReturns = futureValue - totalInvestment;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = futureValue / Math.pow(1 + inflation, n);

    setResult({
      futureValue: futureValue.toFixed(2),
      totalInvestment: totalInvestment.toFixed(2),
      estimatedReturns: estimatedReturns.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateMutualFund();
}, [
  investmentAmount,
  annualReturnRate,
  investmentPeriod,
  inflationRate
]);


  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Mutual Fund Return Calculator</h1>
          <p>
            Calculate the future value of your lump sum mutual fund investment and 
            estimate potential returns over time
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Investment Details
              </h2>

              {/* Investment Amount */}
              <div className="input-group">
                <label>Investment Amount (Lump Sum)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 500000, 1000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setInvestmentAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Annual Return Rate */}
              <div className="input-group">
                <label>Expected Annual Return</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={annualReturnRate}
                    onChange={(e) => setAnnualReturnRate(e.target.value)}
                    className="calc-input"
                    placeholder="12"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8, 10, 12, 15].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setAnnualReturnRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Investment Period */}
              <div className="input-group">
                <label>Investment Period (years)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={investmentPeriod}
                    onChange={(e) => setInvestmentPeriod(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                  />
                </div>
                <div className="quick-select-group">
                  {[5, 10, 15, 20].map((years) => (
                    <button
                      key={years}
                      onClick={() => setInvestmentPeriod(years)}
                      className="quick-select-btn success"
                    >
                      {years}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateMutualFund} className="calculate-btn">
                Calculate Returns
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Investment Summary
              </h2>

              <div className="result-card">
                {/* Total Investment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Investment</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Estimated Returns */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Estimated Returns</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth
                  </div>
                </div>

                {/* Future Value - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Future Value</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {investmentPeriod} years at {annualReturnRate}% returns
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Investment Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Your Investment</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Returns Generated</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Mutual Fund Return Calculator Work?</h3>
              <p>
                The mutual fund return calculator estimates the future value of your lump sum investment by
                considering the initial investment amount, expected annual return rate, and
                investment period. It also accounts for inflation to show the real
                purchasing power of your future wealth.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              A = P × (1 + r)ⁿ
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Principal Investment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Rate of Return</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Time Period in Years</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a mutual fund?</h5>
              <p>
                A mutual fund is an investment vehicle that pools money from multiple investors 
                to invest in a diversified portfolio of stocks, bonds, or other securities. 
                Professional fund managers manage these investments on behalf of investors, 
                making it easier for individuals to access a diversified portfolio.
              </p>
            </div>

            <div className="faq-item">
              <h5>How are mutual fund returns calculated?</h5>
              <p>
                Mutual fund returns are calculated using compound interest on the principal 
                investment. The formula considers the initial investment amount, the expected 
                annual rate of return, and the investment duration to estimate the future value 
                of your investment.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why should I consider inflation in mutual fund calculations?</h5>
              <p>
                Inflation erodes the purchasing power of money over time. The inflation-adjusted 
                value shows what your future returns will actually be worth in today's terms, 
                helping you understand the real growth of your investments and set realistic 
                financial goals.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is a good rate of return for mutual funds?</h5>
              <p>
                Historical data shows that equity mutual funds have delivered returns of 12-15% 
                annually over the long term, while debt mutual funds typically return 7-9%. 
                However, past performance doesn't guarantee future returns. It's wise to use 
                conservative estimates of 10-12% for planning purposes.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I add more money to my mutual fund investment later?</h5>
              <p>
                Yes, you can make additional lump sum investments in your existing mutual fund 
                or start a Systematic Investment Plan (SIP) to invest regularly. Most mutual 
                funds allow additional purchases at any time, though some may have minimum 
                investment requirements.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the minimum investment period for mutual funds?</h5>
              <p>
                While you can redeem mutual fund investments anytime (except ELSS funds which 
                have a 3-year lock-in), it's recommended to stay invested for at least 5-7 years 
                for equity funds. Longer investment periods help ride out market volatility and 
                maximize the benefits of compounding.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MutualFundCalculator;